import { useState, useMemo, useCallback, useEffect, ReactNode } from "react";
import { BriefcaseIcon, ExclamationCircleIcon } from "@heroicons/react/outline";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Checkbox from "@/components/ui/checkbox";
import { useCreateOrganizationRole } from "@/queries/organizations";
import {
  PERMISSION_LABELS,
  PermissionResource,
  Permissions,
} from "@/providers/RbacProvider/constants";
import { EntityIcon } from "@/components/ui/icons";
import { EntityType } from "@/queries/folder-entities/types";

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
}

interface PermissionGroupProps {
  title: string;
  icon: ReactNode;
  permissions: Permissions[];
  selectedPermissions: Permissions[];
  onTogglePermission: (permission: Permissions) => void;
  onToggleActionGroup: (permissions: Permissions[]) => void;
  getActionGroupState: (permissions: Permissions[]) => { checked: boolean; indeterminate: boolean };
  isCreating: boolean;
}

const PermissionGroup = ({
  title,
  icon,
  permissions,
  selectedPermissions,
  onTogglePermission,
  onToggleActionGroup,
  getActionGroupState,
  isCreating,
}: PermissionGroupProps) => {
  const shouldUseSubcolumns = permissions.length > 2 || permissions.length % 2 === 0;
  const actionState = getActionGroupState(permissions);
  const parentCheckboxId = `action-${title}`;

  useEffect(() => {
    const checkbox = document.getElementById(parentCheckboxId) as HTMLInputElement;
    if (checkbox) {
      checkbox.indeterminate = actionState.indeterminate;
    }
  }, [actionState.indeterminate, parentCheckboxId]);

  return (
    <div className="space-y-2">
      <label
        htmlFor={parentCheckboxId}
        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-gray-50"
      >
        <Checkbox
          id={parentCheckboxId}
          checked={actionState.checked}
          onClick={() => onToggleActionGroup(permissions)}
          disabled={isCreating}
          className="!gap-0"
        />
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-900">{title}</span>
        </div>
      </label>
      <div className={`ml-6 grid gap-2 ${shouldUseSubcolumns ? "grid-cols-2" : "grid-cols-1"}`}>
        {permissions.map(permission => {
          const { label } = PERMISSION_LABELS[permission];
          const isSelected = selectedPermissions.includes(permission);

          return (
            <label
              key={permission}
              htmlFor={permission}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-gray-50"
            >
              <Checkbox
                id={permission}
                checked={isSelected}
                onClick={() => onTogglePermission(permission)}
                disabled={isCreating}
                className="!gap-0"
              />
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-900">{label}</span>
                {permission === Permissions.ADMIN && (
                  <span className="text-xs font-bold text-red-500">*</span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const RESOURCE_ORDER: PermissionResource[] = [
  PermissionResource.PROMPTS,
  PermissionResource.WORKFLOWS,
  PermissionResource.DATASETS,
  PermissionResource.EVALUATIONS,
  PermissionResource.WORKSPACE,
];

const RESOURCE_META: Record<PermissionResource, { label: string; icon: ReactNode }> = {
  [PermissionResource.PROMPTS]: {
    label: "Prompts",
    icon: <EntityIcon type={EntityType.PROMPT} size="md" />,
  },
  [PermissionResource.WORKFLOWS]: {
    label: "Workflows",
    icon: <EntityIcon type={EntityType.WORKFLOW} size="md" />,
  },
  [PermissionResource.DATASETS]: {
    label: "Datasets",
    icon: <EntityIcon type={EntityType.DATASET} size="md" />,
  },
  [PermissionResource.EVALUATIONS]: {
    label: "Evaluations",
    icon: <EntityIcon type={EntityType.REPORT} size="md" />,
  },
  [PermissionResource.TOOLS]: {
    label: "Tools",
    icon: <EntityIcon type={EntityType.TOOL} size="md" />,
  },
  [PermissionResource.WORKSPACE]: {
    label: "Workspace",
    icon: <BriefcaseIcon className="h-5 w-5 text-gray-500" />,
  },
};

export const CreateRoleDialog = ({ open, onOpenChange, organizationId }: CreateRoleDialogProps) => {
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<Permissions[]>([]);

  const { mutate: createRole, isPending: isCreating } = useCreateOrganizationRole();

  const groupedPermissionsByResource = useMemo(() => {
    const allPermissions = Object.values(Permissions);
    const grouped = allPermissions.reduce(
      (acc, permission) => {
        const { resource } = PERMISSION_LABELS[permission as Permissions];
        if (!acc[resource]) {
          acc[resource] = [];
        }
        acc[resource].push(permission as Permissions);
        return acc;
      },
      {} as Record<PermissionResource, Permissions[]>,
    );

    return RESOURCE_ORDER.map(resource => {
      const permissionsForResource = (grouped[resource] || []).filter(
        p => PERMISSION_LABELS[p].label !== undefined,
      );
      const sortedPermissions = permissionsForResource.sort((a, b) => {
        const { action: actionA, entity: entityA } = PERMISSION_LABELS[a];
        const { action: actionB, entity: entityB } = PERMISSION_LABELS[b];
        if (actionA !== actionB) {
          return actionA.localeCompare(actionB);
        }
        return entityA.localeCompare(entityB);
      });

      return { resource, permissions: sortedPermissions };
    }).filter(({ permissions: resourcePermissions }) => resourcePermissions.length > 0);
  }, []);

  const handleTogglePermission = useCallback((permission: Permissions) => {
    setSelectedPermissions(prev =>
      prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission],
    );
  }, []);

  const handleToggleActionGroup = useCallback(
    (actionPermissions: Permissions[]) => {
      const allSelected = actionPermissions.every(p => selectedPermissions.includes(p));
      if (allSelected) {
        // Uncheck all permissions in this group
        setSelectedPermissions(prev => prev.filter(p => !actionPermissions.includes(p)));
      } else {
        // Check all permissions in this group
        setSelectedPermissions(prev => {
          const newPermissions = [...prev];
          actionPermissions.forEach(p => {
            if (!newPermissions.includes(p)) {
              newPermissions.push(p);
            }
          });
          return newPermissions;
        });
      }
    },
    [selectedPermissions],
  );

  const getActionGroupState = useCallback(
    (actionPermissions: Permissions[]) => {
      const selectedCount = actionPermissions.filter(p => selectedPermissions.includes(p)).length;
      if (selectedCount === 0) {
        return { checked: false, indeterminate: false };
      }
      if (selectedCount === actionPermissions.length) {
        return { checked: true, indeterminate: false };
      }
      return { checked: false, indeterminate: true };
    },
    [selectedPermissions],
  );

  const handleSave = () => {
    if (!roleName.trim()) {
      return;
    }

    createRole(
      {
        organizationId,
        name: roleName.trim(),
        permissions: selectedPermissions.map(p => p as string),
      },
      {
        onSuccess: () => {
          setRoleName("");
          setSelectedPermissions([]);
          onOpenChange(false);
        },
      },
    );
  };

  const handleClose = () => {
    if (!isCreating) {
      setRoleName("");
      setSelectedPermissions([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] max-w-[572px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Role</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="role-name">Role Name</Label>
            <Input
              id="role-name"
              placeholder="Enter role name"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-4">
            <Label>Permissions</Label>
            <div className="max-h-[400px] space-y-4 overflow-y-auto rounded-md border border-gray-200 bg-white p-4">
              {groupedPermissionsByResource.map(({ resource, permissions }) => {
                const resourceMeta = RESOURCE_META[resource];
                return (
                  <PermissionGroup
                    key={resource}
                    title={resourceMeta.label}
                    icon={resourceMeta.icon}
                    permissions={permissions}
                    selectedPermissions={selectedPermissions}
                    onTogglePermission={handleTogglePermission}
                    onToggleActionGroup={handleToggleActionGroup}
                    getActionGroupState={getActionGroupState}
                    isCreating={isCreating}
                  />
                );
              })}
            </div>
            {selectedPermissions.includes(Permissions.ADMIN) && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <ExclamationCircleIcon className="mt-0.5 h-5 w-5 text-red-500" />
                <div>
                  <div className="font-medium">Workspace admin</div>
                  <div>People in this role will become workspace admins.</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isCreating || !roleName.trim()}>
            {isCreating ? "Creating..." : "Create Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
